import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

export interface HeroSettings {
  tagline: string;
  heading: string;
  subheading: string;
  buttonText: string;
  buttonLink: string;
  image: string;
  bgPosition: string;
  bgSize: string;
}

export const defaultHeroSettings: HeroSettings = {
  tagline: 'Para uma vida feliz ao lado do seu gato',
  heading: 'Tudo sobre o universo felino.',
  subheading: 'Comportamento, saúde e bem-estar para uma vida mais feliz ao lado do seu gato.',
  buttonText: 'Explorar artigos',
  buttonLink: '/blog',
  image:
    'https://readdy.ai/api/search-image?query=Beautiful%20close%20up%20portrait%20of%20tabby%20cat%20with%20bright%20curious%20eyes%20resting%20on%20soft%20knitted%20cream%20blanket%2C%20warm%20natural%20light%2C%20cozy%20home%20atmosphere%2C%20shallow%20depth%20of%20field%2C%20professional%20pet%20photography%2C%20warm%20tones%2C%20high%20detail%20fur&width=900&height=600&seq=hero-cat-main-banner-01&orientation=landscape',
  bgPosition: '80% center',
  bgSize: 'cover',
};

function mapRow(row: Record<string, unknown> | null): HeroSettings {
  if (!row) return { ...defaultHeroSettings };
  return {
    tagline: (row.tagline as string) ?? defaultHeroSettings.tagline,
    heading: (row.heading as string) ?? defaultHeroSettings.heading,
    subheading: (row.subheading as string) ?? defaultHeroSettings.subheading,
    buttonText: (row.button_text as string) ?? defaultHeroSettings.buttonText,
    buttonLink: (row.button_link as string) ?? defaultHeroSettings.buttonLink,
    image: (row.image as string) ?? defaultHeroSettings.image,
    bgPosition: (row.bg_position as string) ?? defaultHeroSettings.bgPosition,
    bgSize: (row.bg_size as string) ?? defaultHeroSettings.bgSize,
  };
}

export function useHeroSettings() {
  const [settings, setSettings] = useState<HeroSettings>(defaultHeroSettings);
  const [loading, setLoading] = useState(true);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('hero_settings')
        .select('*')
        .eq('id', 1)
        .maybeSingle();

      if (error) throw error;
      setSettings(mapRow(data));
    } catch {
      // keep defaults on error
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const saveSettings = async (updated: HeroSettings): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('hero_settings')
        .upsert({
          id: 1,
          tagline: updated.tagline,
          heading: updated.heading,
          subheading: updated.subheading,
          button_text: updated.buttonText,
          button_link: updated.buttonLink,
          image: updated.image,
          bg_position: updated.bgPosition,
          bg_size: updated.bgSize,
          updated_at: new Date().toISOString(),
        });

      if (error) throw error;
      setSettings(updated);
      return true;
    } catch {
      return false;
    }
  };

  return { settings, saveSettings, loading };
}